import { useCallback, useEffect, useRef, useState } from "react";

/** Deliberate pace — easier to follow during the guided assessment. */
export const TTS_SPEECH_RATE = 0.92;

/** Typewriter pace when voice is on — brisk read-ahead, not locked to narration length. */
export function typingDelayWithVoice(baseMs = 20) {
  return Math.min(24, Math.max(16, baseMs + 4));
}

function pickVoice(voices) {
  return (
    voices.find((v) => v.lang.startsWith("en") && v.name.toLowerCase().includes("google")) ||
    voices.find((v) => v.lang.startsWith("en") && v.name.toLowerCase().includes("natural")) ||
    voices.find((v) => v.lang.startsWith("en") && !v.localService) ||
    voices.find((v) => v.lang.startsWith("en")) ||
    voices[0]
  );
}

/* ─── Text-to-Speech ─────────────────────────────────────────────────── */
export function useTTS() {
  const [enabled, setEnabled] = useState(false);
  const [supported] = useState(() => typeof window !== "undefined" && "speechSynthesis" in window);
  const voicesRef = useRef([]);
  const chainRef = useRef(Promise.resolve());

  useEffect(() => {
    if (!supported) return;
    const load = () => { voicesRef.current = window.speechSynthesis.getVoices(); };
    load();
    window.speechSynthesis.addEventListener("voiceschanged", load);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", load);
  }, [supported]);

  const waitForIdle = useCallback(async () => {
    if (!supported) return;
    await chainRef.current;
    while (window.speechSynthesis.speaking) {
      await new Promise((resolve) => setTimeout(resolve, 40));
    }
  }, [supported]);

  const speakAsync = useCallback((text, { interrupt = false } = {}) => {
    if (!enabled || !supported || !text?.trim()) return Promise.resolve();

    const cleaned = String(text).replace(/["'"]/g, "");
    const task = async () => {
      if (interrupt) {
        window.speechSynthesis.cancel();
        await new Promise((resolve) => setTimeout(resolve, 40));
      } else {
        while (window.speechSynthesis.speaking) {
          await new Promise((resolve) => setTimeout(resolve, 40));
        }
      }

      await new Promise((resolve) => {
        const u = new SpeechSynthesisUtterance(cleaned);
        u.rate = TTS_SPEECH_RATE;
        u.pitch = 1.0;
        u.volume = 1.0;
        const preferred = pickVoice(voicesRef.current);
        if (preferred) u.voice = preferred;
        u.onend = resolve;
        u.onerror = resolve;
        window.speechSynthesis.speak(u);
      });
    };

    chainRef.current = chainRef.current.then(task, task);
    return chainRef.current;
  }, [enabled, supported]);

  const speak = useCallback((text, options) => {
    speakAsync(text, options);
  }, [speakAsync]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    chainRef.current = Promise.resolve();
  }, [supported]);

  return { enabled, setEnabled, speak, speakAsync, stop, waitForIdle, supported };
}

/* ─── Speech-to-Text ─────────────────────────────────────────────────── */
export function useSTT() {
  const [listening, setListening] = useState(false);
  const [supported] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  });
  const recRef = useRef(null);

  const start = useCallback((onResult, onEnd) => {
    if (!supported) { onEnd?.(); return; }
    try {
      const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
      const r = new Rec();
      r.continuous = false;
      r.interimResults = false;
      r.lang = "en-US";
      r.onresult = (e) => {
        const transcript = e.results[0]?.[0]?.transcript || "";
        onResult(transcript.trim());
      };
      r.onerror = () => { setListening(false); onEnd?.(); };
      r.onend = () => { setListening(false); onEnd?.(); };
      r.start();
      recRef.current = r;
      setListening(true);
    } catch {
      setListening(false);
      onEnd?.();
    }
  }, [supported]);

  const stop = useCallback(() => {
    recRef.current?.stop();
    setListening(false);
  }, []);

  useEffect(() => () => recRef.current?.stop(), []);

  return { listening, start, stop, supported };
}

/* ─── Match spoken text to rating options ────────────────────────────── */
export function matchVoiceToOption(transcript, options) {
  if (!transcript || !options?.length) return null;
  const t = transcript.toLowerCase().trim();

  // Exact match
  const exact = options.find((o) => o.toLowerCase() === t);
  if (exact) return exact;

  // Contains
  const contains = options.find((o) => t.includes(o.toLowerCase()));
  if (contains) return contains;

  // Key word matching
  const keyMap = {
    "not important": "Not Important",
    "not": "Not Important",
    "important": "Important",
    "very": "Very Important",
    "critical": "Critical",
    "not at all": "Not at all",
    "small": "To a small extent",
    "moderate": "To a moderate extent",
    "great": "To a great extent",
    "completely": "Completely",
    "unable": "Unable to assess",
    "don't know": "Unable to assess",
  };
  for (const [kw, opt] of Object.entries(keyMap)) {
    if (t.includes(kw) && options.includes(opt)) return opt;
  }
  return null;
}
